import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { getString, renderEstimatedCost, renderRecommendations } from './helpers';
import { Shield, FileText, Calendar } from 'lucide-react';

export const TipsTab = ({ generatedItinerary }) => {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="bg-white/90 backdrop-blur-sm border-ocean/20 hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle className="font-serif text-lg text-charcoal flex items-center gap-2">
            <Shield className="w-5 h-5 text-forest" />
            Safety Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          {generatedItinerary.safety_tips ? (
            <div className="space-y-3">
              {Array.isArray(generatedItinerary.safety_tips) ? (
                <ul className="space-y-2">
                  {generatedItinerary.safety_tips.map((tip, index) => (
                    <li key={`safety-${index}`} className="flex gap-2 text-sm text-charcoal">
                      <span className="text-forest">&bull;</span>
                      <span>{getString(tip)}</span>
                    </li>
                  ))}
                </ul>
              ) : typeof generatedItinerary.safety_tips === 'object' ? (
                Object.entries(generatedItinerary.safety_tips).map(([category, content]) => (
                  <div key={category} className="mb-3">
                    <p className="text-xs uppercase tracking-wide text-forest font-semibold mb-1">
                      {category.replace(/_/g, ' ')}
                    </p>
                    <p className="text-sm text-charcoal leading-relaxed">{getString(content)}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-charcoal">{getString(generatedItinerary.safety_tips)}</p>
              )}
            </div>
          ) : (
            <p className="text-charcoal/60">No safety tips available</p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white/90 backdrop-blur-sm border-sky/20 hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle className="font-serif text-lg text-charcoal flex items-center gap-2">
            <FileText className="w-5 h-5 text-burnt" />
            Cultural Awareness
          </CardTitle>
        </CardHeader>
        <CardContent>
          {generatedItinerary.cultural_awareness ? (
            <div className="space-y-3">
              {Array.isArray(generatedItinerary.cultural_awareness) ? (
                <ul className="space-y-2">
                  {generatedItinerary.cultural_awareness.map((item, index) => (
                    <li key={`culture-${index}`} className="flex gap-2 text-sm text-charcoal">
                      <span className="text-burnt">&bull;</span>
                      <span>{getString(item)}</span>
                    </li>
                  ))}
                </ul>
              ) : typeof generatedItinerary.cultural_awareness === 'object' ? (
                Object.entries(generatedItinerary.cultural_awareness).map(([category, content]) => (
                  <div key={category} className="mb-3">
                    <p className="text-xs uppercase tracking-wide text-burnt font-semibold mb-1">
                      {category.replace(/_/g, ' ')}
                    </p>
                    <p className="text-sm text-charcoal leading-relaxed">{getString(content)}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-charcoal">{getString(generatedItinerary.cultural_awareness)}</p>
              )}
            </div>
          ) : (
            <p className="text-charcoal/60">No cultural awareness information available</p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white/90 backdrop-blur-sm border-gold/20 hover:shadow-md transition-shadow md:col-span-2">
        <CardHeader>
          <CardTitle className="font-serif text-lg text-charcoal flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gold" />
            Special Events & Festivals
          </CardTitle>
        </CardHeader>
        <CardContent>
          {generatedItinerary.special_events ? (
            <div className="space-y-3">
              {Array.isArray(generatedItinerary.special_events) ? (
                <ul className="space-y-2">
                  {generatedItinerary.special_events.map((event, index) => (
                    <li key={`event-${index}`} className="flex gap-2 text-sm text-charcoal p-3 bg-gold/5 rounded-lg border border-gold/10">
                      <span className="text-gold">&#9734;</span>
                      <span>{getString(event)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-charcoal">{getString(generatedItinerary.special_events)}</p>
              )}
            </div>
          ) : (
            <p className="text-charcoal/60">No special events information available for this period. Check local event calendars closer to your trip dates.</p>
          )}
        </CardContent>
      </Card>

      {generatedItinerary.estimated_cost && (
        <Card className="bg-white/90 backdrop-blur-sm border-ocean/20 hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="font-serif text-lg text-charcoal flex items-center gap-2">
              Estimated Total Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-forest font-medium text-lg">{renderEstimatedCost(generatedItinerary.estimated_cost)}</p>
          </CardContent>
        </Card>
      )}

      {generatedItinerary.recommendations && (
        <Card className="bg-white/90 backdrop-blur-sm border-sky/20 hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="font-serif text-lg text-charcoal flex items-center gap-2">
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderRecommendations(generatedItinerary.recommendations)}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
